import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {IStation} from '../../../models/Station'
import Clock from '../../assets/icons/Clock.svg'
import NoImage from '../../assets/imgs/no-image.png'
import {ScreenType, StationType} from '../../config/constants'
import {getStationUrl, isCamp, localizeStation} from '../../helpers/station'
import TimeInDay from '../carOrStation/slotForm/TimeInDay'
import ResponsiveImg from '../common/ResponsiveImg'
import Stars from '../common/Stars'
import TypeLabel from '../common/TypeLabel'
import {useLocale, useQuery} from '../hocs/hooks'
import styles from './SingleStation.scss'
import FaMapMarkerAlt from '../../assets/icons/FaMapMarkerAlt.svg'
import { RouterContext } from '../containers/Context'

export const mapsRowWidth = 408 // px
export const mapsRowHeight = 136 // px

const SingleStation = ({station, onSelect, isInfoWindow, viewByList = false, checkIn, checkOut, firstSelection}: {
  station: IStation
  onSelect?: (station: IStation) => any
  isActive?: boolean
  isInfoWindow?: boolean
  viewByList?: boolean
  checkIn?: string
  checkOut?: string
  firstSelection?: boolean
}) => {
  const locale = useLocale()
  const {
    name,
    activityOnly,
    photos,
    stars,
    totalReviews,
    prefecture,
    region,
  } = useMemo(() => localizeStation(locale, station), [locale, station])
  const {history} = useContext(RouterContext)
  const [{url = NoImage, caption} = {}] = photos
  const stationUrlParams = (checkIn && checkOut)
    ? `?checkIn=${checkIn}&checkOut=${checkOut}`
    : ''
  const stationUrl = `${getStationUrl(locale, station, false, false, false)}${stationUrlParams}`
  const localOnSelect = useCallback(
    () => onSelect?.(station) || history.push(stationUrl),
    [history, onSelect, station, stationUrl]
  )
  return <>
  <a
      href={stationUrl}
      onClick={() => {localOnSelect; return false}}
      className={[
        !isInfoWindow && viewByList && 'border rounded w-100',
        'bg-white overflow-hidden c-pointer',
        'd-lg-flex',
        viewByList && 'h-100',
        isInfoWindow && 'align-items-center',
        isInfoWindow && 'align-items-center',
        viewByList ? styles.containerList : styles.containerListMap,
        !viewByList && (firstSelection ? styles.cardMarginFirstLoad : styles.cardMargin)
      ].filter(Boolean).join(' ')}
    >
    <div className={[
      'position-relative',
      'p-lg-1 pl-1',
      viewByList && 'pl-lg-0',
      isInfoWindow && 'pt-1',
      !isInfoWindow && styles.imgMapMode
    ].filter(Boolean).join(' ')}>
      <div className={[
        !isInfoWindow && styles.spImage,
        isInfoWindow
          ? styles.imageMapsCardMapInfo
          : styles.imageCardMapMode
      ].filter(Boolean).join(' ')}>
        <TypeLabel type={activityOnly ? StationType.activity : isCamp(station) ? StationType.camping : StationType.parking}/>
        <ResponsiveImg
          url={url}
          caption={caption}
          className={[
            !isInfoWindow && 'rounded-top',
            !isInfoWindow && styles.roundedMdImg,
          ].filter(Boolean).join(' ')}
        />
      </div>
    </div>
    <div className={[
      !isInfoWindow && 'pt-lg-2 pl-2 pl-13px pb-3 pb-lg-0 position-relative w-100',
      'd-flex flex-column pt-2',
      'pt-lg-1 position-lg-relative',
      isInfoWindow && 'pl-1',
      isInfoWindow
        ? styles.mapCardMapModeInfo
        : styles.textCardMapMode
    ].filter(Boolean).join(' ')}>
      <h2 className={[
        'h5 font-weight-medium ellipsis d-block',
        styles.name,
        isInfoWindow ? 'mb-1' : 'pb-lg-2'
      ].filter(Boolean).join(' ')}>{name}</h2>
      <div
        className={['text-primary font-weight-medium ellipsis', !isInfoWindow && 'mt-1 small'].filter(Boolean).join(' ')}
      >
        <FaMapMarkerAlt className="fa mb-1 mr-1 font-size-8"/>
        <span className={['text-primary', styles.address].filter(Boolean).join(' ')}>{prefecture}/{region}</span>
      </div>
      {/* チェックイン・チェックアウト時間 */}
      {/* <div className={
          ['flex-shrink-0 text-muted small ellipsis', isInfoWindow && 'mt-1']
            .filter(Boolean).join(' ')
      }>
        <Clock className="fa mb-1 mr-1"/>
        {
          station.openTime !== undefined
          && <TimeInDay timeInMs={station.openTime}/>
        } ~ {
          station.closeTime !== undefined
          && <TimeInDay timeInMs={station.closeTime}/>
        }
      </div> */}
      <Stars
        stars={stars}
        total={totalReviews}
        className={['small mt-1', viewByList && 'pb-3 pb-lg-0'].join(' ')}
        starsStyle="font-size-12 font-weight-medium"
      />
      <div className={[
        'd-lg-flex justify-content-lg-between flex-shrink-lg-1 align-items-lg-start',
        isInfoWindow && styles.infoWindowPrice
      ].filter(Boolean).join(' ')}>
        <div className="flex-shrink-0" />
        <div className={[
          'text-primary font-weight-bold h6 mb-0 text-right w-100',
          viewByList && 'mr-2',
          (!viewByList && !isInfoWindow) ? '' : 'mb-md-2'
        ].filter(Boolean).join(' ')}>
          <span>
            <h3 className={
                ['d-inline-block mb-0 mt-1 font-size-24 font-weight-bold', isInfoWindow && 'mt-2']
                  .filter(Boolean).join(' ')
            }>
            <FormattedMessage id="stationThumbnail.price" defaultMessage="{price}JPY〜"
                              values={{price: <FormattedNumber value={
                                station.activityOnly ? station.activityPrice : station.price
                              }/>}}/>
            </h3>
          </span>
          <span className="pl-1 font-weight-medium">
            {activityOnly
              ? <FormattedMessage id="stationThumbnail.activityUnit" defaultMessage="per person" />
              : <FormattedMessage id="stationThumbnail.parkingUnit" defaultMessage="per night" />
            }
          </span>
        </div>
      </div>
    </div>
  </a>
</>
}
SingleStation.displayName = 'SingleStation'
export default SingleStation
